import styles from './OrderSummaryShipment.module.scss'

import React from 'react'

import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'

import { formatStringWithDates } from 'Utilities/DateUtilities/DateUtilities'

import OrderSummaryShipmentStepper from 'Molecules/Stepper/OrderSummaryShipmentStepper/OrderSummaryShipmentStepper'

import Panel from 'Atoms/Panel'
import Svg from 'Atoms/Svg'
import TextLink from 'Atoms/TextLink'
import Typography from 'Atoms/Typography'

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

const formatDeliveryDateRange = (minDate, maxDate) => {
  return minDate !== maxDate ? `${formatDate(minDate)} - ${formatDate(maxDate)}` : formatDate(maxDate)
}

const initiallyExpectedTNTPackageSubtitle = (orderSummary, orderPackage) => {
  const originalMinDate = orderPackage.originalMinDelivery
  const originalMaxDate = orderPackage.originalMaxDelivery
  const currentMaxDate = orderPackage.currentMaxDelivery

  if (!originalMinDate || !originalMaxDate) {
    return null
  }

  if (!(new Date(originalMaxDate) <= new Date(currentMaxDate))) {
    return null
  }

  return (
    <Typography
      size={0.75}
      className={styles.statusSubtitle}
      data-testid={`${orderSummary.orderNumber}_${
        orderPackage.trackingNumber ?? 'unknown'
      }_packageDelayedStatusSubtitle`}
    >
      Initially expected: {formatDeliveryDateRange(originalMinDate, originalMaxDate)}
    </Typography>
  )
}

const packageBackorderedStatusSubtitle = (orderSummary, orderPackage) => {
  if (orderPackage.isDelivered || orderSummary.cancellationDate) {
    return null
  }

  return (
    <Typography
      size={0.75}
      className={styles.statusSubtitle}
      data-testid={`${orderSummary.orderNumber}_${
        orderPackage.trackingNumber ?? 'unknown'
      }_packageBackorderedStatusSubtitle`}
    >
      You will not be charged for this item until it ships.
    </Typography>
  )
}

const PackageSummaryListingTitle = ({ order, orderPackage, packageIndex, totalPackages, isUntrackedPackage }) => {
  function hasBeenPickedUp() {
    return orderPackage.isInStore && orderPackage.onTheWayDate
  }

  if (orderPackage.isDelivered || order.cancellationDate || hasBeenPickedUp()) {
    return null
  }

  if (!orderPackage) {
    return (
      <Typography as="span" font="bold">
        Order Received
      </Typography>
    )
  }

  return (
    <div className={styles.shipmentListingTitle}>
      {!isUntrackedPackage && (
        <Typography as="span">
          Package {packageIndex + 1} of {totalPackages} -{' '}
        </Typography>
      )}
      <Typography
        as="span"
        font="bold"
        style={{ display: 'inline-block' }}
        data-testid={`${order.orderNumber}_${orderPackage?.trackingNumber ?? 'unshipped'}_status`}
      >
        {orderPackage.displayPackageStatus}
      </Typography>
      {orderPackage.isEarly && (
        <span style={{ marginLeft: '2px' }}>
          &#x1F389;
          {/*Party Emoji :D */}
        </span>
      )}
      {(orderPackage.isDelayed || order.isOnHold) && !orderPackage.hideProgressAndWarnings && (
        <span style={{ display: 'inline-block', marginLeft: '8px' }}>
          <Svg icon="warning" size={1} intent="warning" />
        </span>
      )}
    </div>
  )
}

let maxImageThumbnails = 4
const ShipmentImages = ({ publicOrderToken, orderNumber, shipment, onClickToOpenDetails, isCondensed }) => {
  maxImageThumbnails = isCondensed ? 1 : 4

  function combineLineItems(lineItems) {
    const combined = []

    lineItems.forEach((item) => {
      const existingItem = combined.find((combinedItem) => combinedItem.skuVariantNumber === item.skuVariantNumber)

      if (existingItem) {
        existingItem.quantity += item.quantity
      } else {
        combined.push({ ...item })
      }
    })

    return combined
  }

  const combinedLineItems = combineLineItems(shipment.lineItems)

  const shipmentImages = combinedLineItems
    .slice(0, maxImageThumbnails)
    .map(({ imageUrl, searchScore, quantity, lineItemId }, index) => {
      const image = (
        <div
          className={classNames(styles.shipmentImageWrapper, {
            [styles.highlight]: searchScore > 0,
          })}
        >
          <Image
            className={styles.shipmentImage}
            src={imageUrl ? `https:${imageUrl}` : 'https://content.speedwaymotors.com/OtherImages/missingimage2.jpg'}
            alt={'Shipment Image'}
            width={500}
            height={300}
            onClick={!isCondensed && onClickToOpenDetails}
          />
          {quantity > 1 && (
            <Panel
              data-testid={`${orderNumber}_${lineItemId}_lineItemCounter_${quantity}`}
              className={styles.shipmentImageCounter}
              layer={1}
            >
              <Typography size={0.75}>x{quantity}</Typography>
            </Panel>
          )}
        </div>
      )

      if (isCondensed) return <React.Fragment key={index}>{image}</React.Fragment>

      return (
        <Link href={`/account/orderhistory/${publicOrderToken}`} key={index}>
          {image}
        </Link>
      )
    })

  const moreShipmentsText =
    shipment.lineItems.length <= maxImageThumbnails ? null : (
      <TextLink
        underline
        tone="subtle"
        intent="action"
        href={`/account/orderhistory/${publicOrderToken}`}
        size={0.75}
        as="span"
        className={styles.moreText}
        onClick={onClickToOpenDetails}
      >
        + {shipment.lineItems.length - maxImageThumbnails} More
      </TextLink>
    )

  return (
    <div className={styles.shipmentImages}>
      {shipmentImages}
      {moreShipmentsText}
    </div>
  )
}

const ProgressMessage = ({ orderSummary, orderPackage, isCondensed }) => {
  const backorderedItemHasNoShipDate = orderPackage.isBackordered && !orderPackage.estimatedBackorderShipDate

  return (
    <>
      <Typography
        font="bold"
        data-testid={`${orderSummary.orderNumber}_${
          orderPackage?.trackingNumber ?? 'unshipped'
        }_packageStatusDescription`}
      >
        {orderPackage.statusDescriptionString &&
          formatStringWithDates(
            orderPackage.statusDescriptionString.format,
            orderPackage.statusDescriptionString.dates,
            true,
          )}
      </Typography>
      {orderPackage.isDelayed ||
        orderPackage.isEarly ||
        (backorderedItemHasNoShipDate &&
          !isCondensed &&
          initiallyExpectedTNTPackageSubtitle(orderSummary, orderPackage))}
      {orderPackage.isBackordered && !isCondensed && packageBackorderedStatusSubtitle(orderSummary, orderPackage)}
    </>
  )
}

const OrderSummaryShipment = ({
  orderSummary,
  orderPackage,
  totalShipments,
  shipmentIndex,
  isUntrackedPackage,
  className,
  onClickToOpenDetails,
  isCondensed,
}) => {
  let listingTitle = null

  if (!isCondensed)
    listingTitle = (
      <PackageSummaryListingTitle
        order={orderSummary}
        orderPackage={orderPackage}
        packageIndex={shipmentIndex}
        totalPackages={totalShipments}
        isUntrackedPackage={isUntrackedPackage}
      />
    )

  const shipmentImages = (
    <ShipmentImages
      publicOrderToken={orderSummary.publicOrderToken}
      orderNumber={orderSummary.orderNumber}
      shipment={orderPackage}
      onClickToOpenDetails={onClickToOpenDetails}
      isCondensed={isCondensed}
    />
  )
  const progressMessage = !orderPackage.hideProgressAndWarnings ? (
    <ProgressMessage orderSummary={orderSummary} orderPackage={orderPackage} isCondensed={isCondensed} />
  ) : null

  const stepper =
    !orderPackage.hideStepper && !isCondensed ? (
      <OrderSummaryShipmentStepper order={orderSummary} shipment={orderPackage} />
    ) : null

  const containerClasses = classNames(className, styles.orderShipmentContainer)
  // Because the desktop/mobile layouts are so different, we need to have two
  // layouts with duplicate elements
  return (
    <div className={containerClasses}>
      <div className={styles.desktopShipmentLayout}>
        <div className={styles.leftColumn}>
          {listingTitle}
          {shipmentImages}
        </div>
        <div className={styles.rightColumn}>
          {progressMessage}
          {stepper}
        </div>
      </div>

      <div className={styles.mobileShipmentLayout}>
        <div className={styles.mobileStackedContainer}>
          {listingTitle}
          {progressMessage}
        </div>
        {stepper}
        {shipmentImages}
      </div>
    </div>
  )
}

export default OrderSummaryShipment
